import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export interface ICollapsibleBreadcrumbItem {
  /** Visible label, e.g. "Dashboard" */
  title: string;
  /** Route to navigate to on click. Omit for a non-navigable label. */
  to?: string;
  /** Optional leading icon (e.g. Home icon for Dashboard). */
  icon?: React.ReactNode;
}

export interface ICollapsibleBreadcrumbProps {
  /** Ancestor trail shown inside the hover popover (Dashboard, External Leads…). */
  items: ICollapsibleBreadcrumbItem[];
  /** The current page label shown inline, e.g. "Edit Lead · VISHWAKARMA FURNITURE". */
  current: string;
  /** Back-control handler. Defaults to browser back — navigate(-1). */
  onBack?: () => void;
  /** Label for the back control. Defaults to "Back". */
  backLabel?: string;
}

/**
 * A compact breadcrumb that collapses the ancestor trail behind a single
 * "← Back" control. Hovering (or focusing/clicking) the control reveals a
 * popover listing the ancestor links. The current page label stays inline.
 *
 * Hover UX uses the standard MUI pattern: the Popover root has
 * `pointerEvents: none` while its paper re-enables `pointerEvents: auto`, and a
 * small close delay bridges the gap between the anchor and the popover so the
 * user can move the cursor onto the menu without it closing.
 */
export const CollapsibleBreadcrumb: React.FC<ICollapsibleBreadcrumbProps> = ({
  items,
  current,
  onBack,
  backLabel = "Back",
}) => {
  const navigate = useNavigate();
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = React.useState(false);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleOpen = () => {
    clearCloseTimer();
    if (items.length) setOpen(true);
  };

  const handleCloseDeferred = () => {
    clearCloseTimer();
    // Small delay lets the cursor travel from the anchor onto the popover paper.
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  React.useEffect(() => () => clearCloseTimer(), []);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  const handleItemClick = (item: ICollapsibleBreadcrumbItem) => {
    setOpen(false);
    if (item.to) navigate(item.to);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      {/* Back control + hover anchor */}
      <Box
        ref={anchorRef}
        onMouseEnter={handleOpen}
        onMouseLeave={handleCloseDeferred}
        sx={{ display: "inline-flex" }}
      >
        <Button
          size="small"
          color="inherit"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={handleBack}
          onFocus={handleOpen}
          onBlur={handleCloseDeferred}
          aria-haspopup={items.length ? "true" : undefined}
          aria-expanded={open ? "true" : undefined}
          sx={{
            fontWeight: 600,
            textTransform: "none",
            color: "text.secondary",
            px: 1,
            "&:hover": { bgcolor: "action.hover", color: "text.primary" },
          }}
        >
          {backLabel}
        </Button>
      </Box>

      {current && <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />}

      {/* Current page label */}
      {current && (
        <Typography
          variant="subtitle1"
          noWrap
          title={current}
          sx={{ fontWeight: 700, minWidth: 0, color: "text.primary" }}
        >
          {current}
        </Typography>
      )}

      {/* Hover / focus popover with the ancestor trail */}
      {items.length > 0 && (
        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          disableRestoreFocus
          disableAutoFocus
          disableEnforceFocus
          // Let hover pass through the backdrop-less root.
          sx={{ pointerEvents: "none" }}
          PaperProps={{
            onMouseEnter: handleOpen,
            onMouseLeave: handleCloseDeferred,
            sx: {
              pointerEvents: "auto",
              mt: 0.5,
              minWidth: 200,
              boxShadow: 3,
              borderRadius: 1.5,
            },
          }}
        >
          <List dense disablePadding sx={{ py: 0.5 }}>
            {items.map((item) => (
              <ListItemButton
                key={item.title}
                onClick={() => handleItemClick(item)}
                disabled={!item.to}
                sx={{ py: 0.5, px: 1.5 }}
              >
                {item.icon && (
                  <ListItemIcon sx={{ minWidth: 32, color: "primary.main" }}>
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    textTransform: "capitalize",
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Popover>
      )}
    </Stack>
  );
};
